import { useInterval } from '@src/core';
import { FC, PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import NotificationModal from './notification-modal';
import NotificationSnackbar from './notification-snackbar';
import { ExtendedAlert } from './types';

const DEFAULT_SNACKBAR_CHECK_DELAY = 200;

/**
 * Context
 */
interface NotificationsContextData {
  dispatchNotificationModal: (m: ExtendedAlert) => void;
  dispatchSnackbarMessage: (m: ExtendedAlert) => void;
}

const NotificationsContext = createContext<NotificationsContextData>({} as NotificationsContextData);

const NotificationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [modalMessage, dispatchNotificationModal] = useState<ExtendedAlert>();
  const [modalState, changeModalState] = useState(false);

  const [currentSnackbarMessage, setCurrentSnackbarMessage] = useState<ExtendedAlert>();
  const [snackbarMessages, setSnackbarMessages] = useState<ExtendedAlert[]>([]);
  const [snackbarState, changeSnackbarState] = useState(false);

  const dispatchSnackbarMessage = (m: ExtendedAlert) => {
    setSnackbarMessages(current => [...current, m]);
  };

  useEffect(() => {
    if (!modalMessage) return;
    changeModalState(true);
  }, [modalMessage]);

  useInterval(() => {
    if (!snackbarState && snackbarMessages.length > 0) {
      const message = snackbarMessages.shift();
      setCurrentSnackbarMessage(message);
      changeSnackbarState(true);
    }
  }, DEFAULT_SNACKBAR_CHECK_DELAY);

  return (
    <NotificationsContext.Provider
      value={{
        dispatchNotificationModal,
        dispatchSnackbarMessage,
      }}
    >
      <NotificationModal changeModalState={changeModalState} modalOpened={modalState} message={modalMessage} />
      <NotificationSnackbar
        changeSnackbarState={changeSnackbarState}
        snackbarOpened={snackbarState}
        message={currentSnackbarMessage}
      />
      {children}
    </NotificationsContext.Provider>
  );
};

const useNotificationsContext = (): NotificationsContextData => useContext(NotificationsContext);

export { NotificationsProvider, useNotificationsContext };
